import type { Joi } from '@ksyos/koi';
import { Koi } from '@ksyos/koi';
import { never } from '../utils/never';

export enum SettingReferenceType {
    profileServiceEmployments = 'profileServiceEmployments',
    serviceEmployment = 'serviceEmployment',
    serviceOrganizationSettings = 'serviceOrganizationSettings',
}

export const settingDefinitionList = {
    canEditGroupSession: 'boolean' as const,
    canEditMdo: 'boolean' as const,
    canExportForDato: 'boolean' as const,
    canReadBariPreDashboard: 'boolean' as const,
    canReadDashboard: 'boolean' as const,
    canReadGroupSession: 'boolean' as const,
    canReadMdo: 'boolean' as const,
    canReadOsasDashboard: 'boolean' as const,
    canReadQuestionnaireResponses: 'boolean' as const,
    canReadTotalWeightLoss: 'boolean' as const,
    canRegisterGroupSessionAttendance: 'boolean' as const,
    canStartProcess: 'boolean' as const,
    isAutoCloseRequest: 'boolean' as const,
    isRecipient: 'boolean' as const,
    isRequestRecipient: 'boolean' as const,
    isRequester: 'boolean' as const,
    receiveZorgmailOnConsultOpen: 'boolean' as const,
    sendNotificationEmployment: 'boolean' as const,
    sendNotificationPractitioner: 'boolean' as const,
    sendNotificationPractitionerWhenDeviant: 'boolean' as const,
    serviceGroupSettingId: 'string' as const,
    someNumberSetting: 'number' as const,
    spirometerSoftwareName: 'string' as const,
    zorgmailNummer: 'string' as const,
};

export type SettingsType = {
    [name in keyof typeof settingDefinitionList]: TypeNameToType<(typeof settingDefinitionList)[name]>;
};
export type SettingName = keyof SettingsType;
export type SettingType = boolean | string | number | null;
export type TypeOfSetting<Name extends SettingName> = SettingsType[Name];

export function parseSettingValue<Name extends SettingName>(name: Name, value: string): TypeOfSetting<Name> {
    // FIXME: When a string value is `null` it will be parsed as `'null'` (e.g. `zorgmailNummer`)

    const type = settingDefinitionList[name] as 'string' | 'boolean' | 'number';
    switch (type) {
        case 'string':
            return value as TypeOfSetting<Name>;
        case 'boolean':
            return (value === 'true') as TypeOfSetting<Name>;
        case 'number':
            return parseInt(value, 10) as TypeOfSetting<Name>;
        default:
            return never(type);
    }
}

type TypeMap = {
    boolean: boolean;
    number: number;
    string: string;
};

type TypeNameToType<Type extends keyof TypeMap> = TypeMap[Type];

function validationKeyForSetting(name: SettingName) {
    const type = settingDefinitionList[name] as 'string' | 'boolean' | 'number';
    switch (type) {
        case 'string':
            return Koi.string().max(1000).allow(null);
        case 'boolean':
            return Koi.boolean().allow(null);
        case 'number':
            return Koi.number().allow(null);
        default:
            return never(type);
    }
}

const settingsValidationKeys: {
    [name in keyof typeof settingDefinitionList]?: Joi.AnySchema;
} = {};
for (const name of Object.keys(settingDefinitionList) as SettingName[]) {
    settingsValidationKeys[name] = validationKeyForSetting(name);
}
export const settingsValidationSchema = Koi.object().keys(settingsValidationKeys).options({ presence: 'optional' });
